import { useCallback, useMemo } from 'react';
import { CONTENT_PLATFORM_PROPERTY_NAME, getAssociatedContentTranslations, getContentPropertyValue } from 'content-management-data-lib';
import { publish as publishReconciliation, unpublish as unpublishReconciliation, changeAudienceAccess as changeAudienceAccessReconciliation, assignPermissions as assignPermissionsReconciliation } from 'content-management-actions-lib/utils/reconciliationUtils';
import listingLibObserver from 'framework-listing-lib/utils/listingLibObserver';
import { CONTENT_ACTION_IDS } from 'content-management-actions-lib/constants/actionIds';
const useActionSuccessUpdateHandlers = ({
  actionSuccessHandlerOverrides = {},
  allActions
}) => {
  const publish = useCallback(({
    completedResults,
    actionData
  }) => {
    const updatedContentObjects = publishReconciliation(completedResults, actionData.scheduledDate);
    listingLibObserver.update(updatedContentObjects);
  }, []);
  const unpublish = useCallback(({
    completedResults,
    actionData
  }) => {
    const updatedContentObjects = unpublishReconciliation(completedResults, actionData.scheduledDate);
    listingLibObserver.update(updatedContentObjects);
  }, []);
  const deleteContent = useCallback(({
    completedResults
  }) => {
    const deletedObjectIds = completedResults.map(({
      objectId
    }) => objectId);
    listingLibObserver.delete(deletedObjectIds);
  }, []);
  const clone = useCallback(({
    completedResults: [crmObject]
  }) => {
    if (allActions.edit && allActions.edit.onActionButtonClick) {
      allActions.edit.onActionButtonClick(crmObject);
    }
  }, [allActions]);

  // Multi-language actions
  const createLanguageVariation = useCallback(({
    completedResults: [crmObject],
    actionData
  }) => {
    const newVariationId = actionData.objectId;
    if (newVariationId) {
      const newLanguageVariation = Object.assign({}, crmObject, {
        objectId: newVariationId,
        properties: Object.assign({}, crmObject.properties, {
          [CONTENT_PLATFORM_PROPERTY_NAME.ID]: {
            value: `${newVariationId}`,
            rawValue: newVariationId
          }
        })
      });
      if (allActions.edit && allActions.edit.onActionButtonClick) {
        allActions.edit.onActionButtonClick(newLanguageVariation);
      }
    }
  }, [allActions]);
  const removeFromLanguageGroup = useCallback(({
    completedResults: [crmObject]
  }) => {
    const primaryId = getContentPropertyValue(crmObject, CONTENT_PLATFORM_PROPERTY_NAME.TRANSLATED_FROM_ID);
    listingLibObserver.removeFromMultiLanguageGroup(primaryId, crmObject);
  }, []);
  const convertToLanguageGroupPrimary = useCallback(({
    completedResults: [crmObject]
  }) => {
    const primaryId = getContentPropertyValue(crmObject, CONTENT_PLATFORM_PROPERTY_NAME.TRANSLATED_FROM_ID);
    listingLibObserver.makePrimaryGroup(primaryId, crmObject);
  }, []);
  const switchLanguageGroupPrimary = useCallback(({
    completedResults: [crmObject],
    actionData
  }) => {
    const newPrimaryId = actionData.newPrimaryId;
    if (newPrimaryId) {
      const newPrimaryCrmObject = getAssociatedContentTranslations(crmObject).find(t => t.objectId === newPrimaryId);
      if (newPrimaryCrmObject) {
        listingLibObserver.makePrimaryGroup(crmObject.objectId, newPrimaryCrmObject);
      }
    }
  }, []);
  const addToLanguageGroup = useCallback(({
    actionData,
    completedResults
  }) => {
    const {
      primaryId
    } = actionData;
    completedResults.forEach(crmObject => {
      listingLibObserver.moveToPrimaryGroup(primaryId, crmObject);
    });
  }, []);
  const changeAudienceAccess = useCallback(({
    completedResults,
    actionData
  }) => {
    const {
      accessRuleType,
      accessRuleIds,
      password
    } = actionData;
    const updatedContentObjects = changeAudienceAccessReconciliation(completedResults, {
      accessRuleType,
      accessRuleIds,
      password
    });
    listingLibObserver.update(updatedContentObjects);
  }, []);
  const assignPermissions = useCallback(({
    completedResults,
    actionData
  }) => {
    const {
      teamIds,
      userIds
    } = actionData;
    const updatedContentObjects = assignPermissionsReconciliation(completedResults, {
      teamIds,
      userIds
    });
    listingLibObserver.update(updatedContentObjects);
  }, []);
  return useMemo(() => Object.assign({
    [CONTENT_ACTION_IDS.ARCHIVE]: deleteContent,
    [CONTENT_ACTION_IDS.UNARCHIVE]: deleteContent,
    [CONTENT_ACTION_IDS.PUBLISHING_OPTIONS]: publish,
    [CONTENT_ACTION_IDS.UNPUBLISHING_OPTIONS]: unpublish,
    [CONTENT_ACTION_IDS.DELETE]: deleteContent,
    [CONTENT_ACTION_IDS.DELETE_LANGUAGE_GROUP]: deleteContent,
    [CONTENT_ACTION_IDS.CONTROL_AUDIENCE_ACCESS]: changeAudienceAccess,
    [CONTENT_ACTION_IDS.CLONE]: clone,
    [CONTENT_ACTION_IDS.ASSIGN_PERMISSIONS]: assignPermissions,
    // mLang actions
    [CONTENT_ACTION_IDS.CREATE_LANGUAGE_VARIANT]: createLanguageVariation,
    [CONTENT_ACTION_IDS.REMOVE_FROM_LANGUAGE_GROUP]: removeFromLanguageGroup,
    [CONTENT_ACTION_IDS.CONVERT_TO_LANGUAGE_GROUP_PRIMARY]: convertToLanguageGroupPrimary,
    [CONTENT_ACTION_IDS.ADD_TO_LANGUAGE_GROUP]: addToLanguageGroup,
    [CONTENT_ACTION_IDS.SWITCH_LANGUAGE_GROUP_PRIMARY]: switchLanguageGroupPrimary
  }, actionSuccessHandlerOverrides), [actionSuccessHandlerOverrides, addToLanguageGroup, assignPermissions, changeAudienceAccess, clone, convertToLanguageGroupPrimary, createLanguageVariation, deleteContent, publish, removeFromLanguageGroup, switchLanguageGroupPrimary, unpublish]);
};
export default useActionSuccessUpdateHandlers;